import type React from 'react';
import type { PickedContact } from '../../../../lib/contact-references';
import { useTranslation } from '../../../../lib/i18n';

// The pending picked-contacts row — a removable chip per contact the user attached via "+ Contact",
// shown beside the attachment chips above the composer until the message is sent. Mirrors
// AttachmentChipRow's shape/tokens.
export function ContactChipRow({
  contacts,
  onRemove,
}: {
  contacts: PickedContact[];
  onRemove: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div style={styles.row}>
      {contacts.map((c) => (
        <div key={c.id} title={c.phones[0] || c.emails[0] || c.name} style={styles.chip}>
          <span style={styles.icon} aria-hidden>
            <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
              <circle cx="9" cy="6" r="3" stroke="currentColor" strokeWidth="1.5" />
              <path d="M3.5 15a5.5 5.5 0 0 1 11 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </span>
          <span style={styles.name}>{c.name}</span>
          <button type="button" aria-label={t('chat.contacts.remove')} onClick={() => onRemove(c.id)} style={styles.remove}>
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

const styles = {
  row: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 } satisfies React.CSSProperties,
  chip: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 10px',
    paddingInlineEnd: 28,
    background: 'rgba(var(--hairline-rgb),0.04)',
    border: 'none',
    borderRadius: 'var(--sym-radius-badge)',
    maxWidth: 240,
  } satisfies React.CSSProperties,
  icon: {
    display: 'inline-flex',
    alignItems: 'center',
    color: 'rgba(var(--ink-rgb),0.6)',
  } satisfies React.CSSProperties,
  name: {
    minWidth: 0,
    fontSize: 12,
    color: 'rgba(var(--ink-rgb),0.85)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 150,
  } satisfies React.CSSProperties,
  remove: {
    position: 'absolute',
    top: 4,
    insetInlineEnd: 4,
    width: 18,
    height: 18,
    padding: 0,
    background: 'rgba(var(--ds-black-rgb), 0.4)',
    border: 'none',
    borderRadius: '50%',
    color: 'rgba(var(--ink-rgb),0.85)',
    cursor: 'pointer',
    fontSize: 12,
    lineHeight: 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  } satisfies React.CSSProperties,
};
